import {
  IntegrationActivityEventType,
  PropertyOperation,
  PropertyPortal,
  PropertyPortalPublicationStatus,
} from 'src/api/generated';
import type { CatalogProperty } from 'src/api/generated';
import type { IntegrationActivityEvent } from 'src/api/generated';
import type { PropertyPortalPublication } from 'src/api/generated';
import { isRecord } from 'src/modules/shared/utils/types.util';
import { getPublicationPortalTarget } from './property-meli-publishing-manager.util';

export type SupportedMeliOperation =
  | PropertyOperation.SALE
  | PropertyOperation.RENT;
export type MeliPublicationAction = 'publish' | 'pause' | 'unpublish';

const compareIsoDatesDesc = (
  left: string | null | undefined,
  right: string | null | undefined
): number => {
  const normalizedLeft = left ?? '';
  const normalizedRight = right ?? '';

  return normalizedRight.localeCompare(normalizedLeft);
};

export const getSupportedMeliOperations = (
  property: CatalogProperty
): SupportedMeliOperation[] => {
  if (property.operation == null) {
    return [];
  }

  switch (property.operation) {
    case PropertyOperation.SALE:
      return [PropertyOperation.SALE];
    case PropertyOperation.RENT:
    case PropertyOperation.TEMPORARY_RENT:
      return [PropertyOperation.RENT];
    case PropertyOperation.SALE_AND_RENT:
      return [PropertyOperation.SALE, PropertyOperation.RENT];
    default:
      return [];
  }
};

export const getSupportedMeliOperationFromPublication = (
  operation: PropertyPortalPublication['operation']
): SupportedMeliOperation | null => {
  if (operation == null) {
    return null;
  }

  switch (operation) {
    case PropertyOperation.SALE:
      return PropertyOperation.SALE;
    case PropertyOperation.RENT:
    case PropertyOperation.TEMPORARY_RENT:
      return PropertyOperation.RENT;
    case PropertyOperation.SALE_AND_RENT:
      return null;
    default:
      return null;
  }
};

export const getCurrentMeliPublication = (
  publications: PropertyPortalPublication[],
  operation: SupportedMeliOperation
): PropertyPortalPublication | null => {
  const matchingPublications = sortMeliPublicationsByRecency(
    publications.filter(
      (publication) =>
        publication.portal === PropertyPortal.MELI &&
        publication.operation === operation
    )
  );
  const latestPublication = matchingPublications.at(0) ?? null;

  return (
    matchingPublications.find(
      (publication) =>
        !isTerminalMeliPublication(publication) &&
        !isSupersededMeliPublication(publication, latestPublication)
    ) ??
    latestPublication ??
    null
  );
};

export const getMeliPublicationById = (
  publications: PropertyPortalPublication[],
  publicationId: number | null
): PropertyPortalPublication | null => {
  if (publicationId == null) {
    return null;
  }

  return (
    publications.find(
      (publication) =>
        publication.portal === PropertyPortal.MELI &&
        publication.id === publicationId
    ) ?? null
  );
};

export const getMeliPublicationConfigString = (
  publication: PropertyPortalPublication | null,
  key: string
): string | null => {
  const config = publication?.config;
  if (!isRecord(config)) {
    return null;
  }

  const value = config[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

export const getMeliPublicationConfigStringArray = (
  publication: PropertyPortalPublication | null,
  key: string
): string[] => {
  const config = publication?.config;
  if (!isRecord(config)) {
    return [];
  }

  const value = config[key];
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (item): item is string => typeof item === 'string' && item.trim().length > 0
  );
};

export const getMeliPublicationConfigBoolean = (
  publication: PropertyPortalPublication | null,
  key: string
): boolean | null => {
  const config = publication?.config;
  if (!isRecord(config)) {
    return null;
  }

  const value = config[key];
  return typeof value === 'boolean' ? value : null;
};

export const getMeliPublicationProviderStatus = (
  publication: PropertyPortalPublication | null
): string | null =>
  getMeliPublicationConfigString(publication, 'lastProviderStatus');

/** Current billing-related pause; availability remains controlled by the property. */
export const getMeliBillingPause = (
  publication: PropertyPortalPublication | null
): 'billing' | 'after_billing' | null => {
  const value = getMeliPublicationConfigString(publication, 'billingPause');
  return value === 'billing' || value === 'after_billing' ? value : null;
};

/** Billing suspension is distinct from MeLi's payment_required publication status. */
export const isMeliPublicationBillingBlocked = (
  publication: PropertyPortalPublication | null
): boolean => getMeliBillingPause(publication) === 'billing';

export const isMeliPublicationPaymentRequired = (
  publication: PropertyPortalPublication | null
): boolean =>
  getMeliPublicationConfigBoolean(publication, 'paymentRequired') === true ||
  getMeliPublicationProviderStatus(publication) === 'payment_required';

export const getUniqueMeliPublicationTargets = (
  publications: PropertyPortalPublication[]
): ReturnType<typeof getPublicationPortalTarget>[] =>
  Array.from(
    new Set(
      publications.map((publication) => getPublicationPortalTarget(publication))
    )
  );

export const canPauseMeliPublication = (
  publication: PropertyPortalPublication | null
): boolean => publication?.status === PropertyPortalPublicationStatus.PUBLISHED;

export const canPublishMeliPublication = (
  publication: PropertyPortalPublication | null
): boolean =>
  publication != null &&
  !isMeliPublicationBillingBlocked(publication) &&
  [
    PropertyPortalPublicationStatus.DRAFT,
    PropertyPortalPublicationStatus.ERROR,
    PropertyPortalPublicationStatus.PAUSED,
    PropertyPortalPublicationStatus.UNPUBLISHED,
  ].includes(publication.status);

export const canUnpublishMeliPublication = (
  publication: PropertyPortalPublication | null
): boolean =>
  publication != null &&
  [
    PropertyPortalPublicationStatus.PUBLISHED,
    PropertyPortalPublicationStatus.PAUSED,
  ].includes(publication.status);

export const isTerminalMeliPublication = (
  publication: PropertyPortalPublication
): boolean =>
  publication.status === PropertyPortalPublicationStatus.UNPUBLISHED ||
  getMeliPublicationProviderStatus(publication) === 'closed' ||
  getMeliPublicationProviderStatus(publication) === 'inactive' ||
  getMeliPublicationProviderStatus(publication) === 'deleted';

export const isSupersededMeliPublication = (
  publication: PropertyPortalPublication,
  latestPublication: PropertyPortalPublication | null
): boolean =>
  latestPublication != null &&
  latestPublication.id !== publication.id &&
  isMeliPublicationPaymentRequired(publication);

export const sortMeliPublicationsByRecency = (
  publications: PropertyPortalPublication[]
): PropertyPortalPublication[] =>
  [...publications].sort((left, right) => {
    const updatedAtComparison = compareIsoDatesDesc(
      left.updatedAt,
      right.updatedAt
    );

    if (updatedAtComparison !== 0) {
      return updatedAtComparison;
    }

    const createdAtComparison = compareIsoDatesDesc(
      left.createdAt,
      right.createdAt
    );

    if (createdAtComparison !== 0) {
      return createdAtComparison;
    }

    return right.id - left.id;
  });

export const shouldContinueMeliPublicationPolling = (
  publication: PropertyPortalPublication | null,
  action: MeliPublicationAction | null
): boolean => {
  if (!action) {
    return false;
  }

  if (!publication) {
    return action === 'publish';
  }

  if (publication.status === PropertyPortalPublicationStatus.ERROR) {
    return false;
  }

  switch (action) {
    case 'publish':
      return publication.status !== PropertyPortalPublicationStatus.PUBLISHED;
    case 'pause':
      return publication.status !== PropertyPortalPublicationStatus.PAUSED;
    case 'unpublish':
      return publication.status !== PropertyPortalPublicationStatus.UNPUBLISHED;
    default:
      return false;
  }
};

export const getLatestPublicationEvent = (
  events: IntegrationActivityEvent[] | undefined
): IntegrationActivityEvent | null => {
  if (!events || events.length === 0) {
    return null;
  }

  return (
    [...events].sort((left, right) =>
      right.createdAt.localeCompare(left.createdAt)
    )[0] ?? null
  );
};

export const isMeliSyncPublicationEvent = (
  event: IntegrationActivityEvent
): boolean =>
  event.type === IntegrationActivityEventType.SYNC_APPLIED ||
  event.type === IntegrationActivityEventType.SYNC_FAILED ||
  event.type === IntegrationActivityEventType.SYNC_SKIPPED ||
  event.type === IntegrationActivityEventType.SYNC_RECONCILIATION_APPLIED;

export const getLatestSyncPublicationEvent = (
  events: IntegrationActivityEvent[] | undefined
): IntegrationActivityEvent | null => {
  if (!events || events.length === 0) {
    return null;
  }

  return (
    [...events]
      .filter(isMeliSyncPublicationEvent)
      .sort((left, right) =>
        right.createdAt.localeCompare(left.createdAt)
      )[0] ?? null
  );
};

export const getMeliDisplayStatus = (params: {
  publication: PropertyPortalPublication | null;
  events?: IntegrationActivityEvent[];
}): PropertyPortalPublication['status'] | 'queued_publish' | 'unpublished' => {
  if (!params.publication) {
    return 'unpublished';
  }

  const latestEvent = getLatestPublicationEvent(params.events);
  if (
    params.publication.status === PropertyPortalPublicationStatus.DRAFT &&
    latestEvent?.type ===
      IntegrationActivityEventType.PUBLICATION_PUBLISH_ENQUEUED
  ) {
    return 'queued_publish';
  }

  return params.publication.status;
};
